import { jsonAvecCitationsAffichees, localisateurAffiche } from '../domaine/citations';
// Composants partagés par les écrans : état de chargement, erreur, étiquettes, provenance, différence exacte.
import type { ReactNode } from 'react';
import { expliquerErreur } from '../api/client';
import type { Changement, Citation, EtatCollecteur, EtatSource, Gravite, Provenance } from '../api/types';
import { aplatirChangement, cheminLisible, estTechnique } from '../domaine/corrections';
import { LIBELLE_ETAT_COLLECTEUR, TON_ETAT_COLLECTEUR } from '../domaine/collecteurs';
import { LIBELLE_ETAT_SOURCE, TON_ETAT_SOURCE } from '../domaine/sources';
import { importMarque, texteValidationImportee } from '../domaine/importation';
import { dateFr, LIBELLE_GRAVITE, LIBELLE_STATUT_FAIT, noteLisible, valeurLisible } from '../domaine/format';

export function Chargement({ quoi }: { quoi: string }) {
  return (
    <p className="chargement" role="status">
      Lecture {quoi}…
    </p>
  );
}

export function Erreur({ erreur, titre }: { erreur: unknown; titre?: string }) {
  return (
    <div className="encadre erreur" role="alert">
      <p>
        <b>{titre ?? 'L’API a refusé la demande.'}</b>
      </p>
      <p>{expliquerErreur(erreur)}</p>
    </div>
  );
}

/** Attend plusieurs lectures ; affiche le chargement ou la première erreur. */
export function Attendre({
  etats,
  quoi,
  children,
}: {
  etats: { isPending: boolean; error: unknown }[];
  quoi: string;
  children: () => ReactNode;
}) {
  const err = etats.find((e) => e.error)?.error;
  if (err) return <Erreur erreur={err} titre={`Impossible de lire ${quoi}.`} />;
  if (etats.some((e) => e.isPending)) return <Chargement quoi={quoi} />;
  return <>{children()}</>;
}

export function Etiquette({ ton = 'info', children }: { ton?: 'info' | 'ok' | 'attention' | 'grave' | 'neutre'; children: ReactNode }) {
  return <span className={`etiquette ${ton === 'info' ? '' : ton}`}>{children}</span>;
}

/** Identifiant court (non secret) de l'auteur, en texte simple ; rien pour une entrée ancienne (O80). */
export function IdCourt({ x }: { x: { par_id_court?: string | null } }) {
  return x.par_id_court ? <> · id {x.par_id_court}</> : null;
}

/**
 * Mention d'alerte (O80) : le nom mêle plusieurs écritures ou ressemble au nom d'un autre compte. Informative seulement :
 * elle ne bloque et n'autorise rien, l'API reste seule juge.
 */
export function AlerteNomSuspect({ x }: { x: { par_nom_suspect?: boolean | null } }) {
  if (!x.par_nom_suspect) return null;
  return (
    <small className="note" role="note" style={{ display: 'block', fontWeight: 600 }}>
      Nom inhabituel : vérifiez cette personne par un autre moyen avant d’approuver
    </small>
  );
}

export function EtiquetteEtatSource({ etat }: { etat: EtatSource }) {
  return <Etiquette ton={TON_ETAT_SOURCE[etat]}>{LIBELLE_ETAT_SOURCE[etat]}</Etiquette>;
}

export function EtiquetteEtatCollecteur({ etat }: { etat: EtatCollecteur }) {
  return <Etiquette ton={TON_ETAT_COLLECTEUR[etat]}>{LIBELLE_ETAT_COLLECTEUR[etat]}</Etiquette>;
}

export function EtiquetteGravite({ g }: { g: Gravite | null | undefined }) {
  if (!g) return <Etiquette ton="neutre">Gravité inconnue</Etiquette>;
  const ton = g === 'critique' || g === 'elevee' ? 'grave' : g === 'moyenne' ? 'attention' : 'neutre';
  return <Etiquette ton={ton}>{LIBELLE_GRAVITE[g]}</Etiquette>;
}

export function EtiquetteStatut({ statut }: { statut: string | undefined }) {
  const s = statut ?? 'propose';
  const ton = s === 'confirme' ? 'ok' : s === 'corrige' ? 'info' : s === 'rejete' ? 'grave' : 'attention';
  return <Etiquette ton={ton}>{LIBELLE_STATUT_FAIT[s] ?? s}</Etiquette>;
}

export function Jauge({ valeur, libelle }: { valeur: number; libelle: string }) {
  return (
    <span className="ligne" style={{ gap: 6 }}>
      <span className="jauge" role="img" aria-label={`${libelle} : ${Math.round(valeur * 100)} %`}>
        <i style={{ width: `${Math.round(valeur * 100)}%` }} />
      </span>
      <span className="num">{Math.round(valeur * 100)} %</span>
    </span>
  );
}

export function Citations({ citations }: { citations: Citation[] }) {
  if (!citations.length) return <p className="note">Aucun extrait cité.</p>;
  return (
    <div className="pile" style={{ gap: 6 }}>
      {citations.map((c, i) => (
        <div className="citation" key={`${c.source}-${c.localisateur}-${i}`}>
          <q>{c.extrait}</q>
          <span className="src">
            {c.source} · {localisateurAffiche(c.localisateur)}
          </span>
        </div>
      ))}
    </div>
  );
}

export function BlocProvenance({ p, titre }: { p: Provenance | undefined; titre?: string }) {
  if (!p) return <p className="note">Provenance non renseignée (fait repris de l’ontologie sans citation propre).</p>;
  const importe = importMarque(p);
  return (
    <div className="pile" style={{ gap: 8 }}>
      {titre && <h3>{titre}</h3>}
      <div className="ligne">
        <EtiquetteStatut statut={p.statut} />
        <Jauge valeur={p.confiance ?? 1} libelle="Confiance" />
        {p.methode && <span className="note">méthode : {p.methode}</span>}
      </div>
      {importe ? (
        <p className="note" data-testid="validation-importee">
          {texteValidationImportee(importe)}
        </p>
      ) : (
        (p.valide_par || p.valide_le) && (
          <p className="note">
            {p.statut === 'corrige' ? 'Corrigé' : 'Validé'} par {p.valide_par ?? '—'} le {dateFr(p.valide_le)}
          </p>
        )
      )}
      {p.note && (
        <>
          <p className="note">Note : {noteLisible(p.note)}</p>
          {noteLisible(p.note) !== p.note && (
            <details className="note">
              <summary>Détails techniques</summary>
              Note d’origine : {p.note}
            </details>
          )}
        </>
      )}
      <Citations citations={p.citations ?? []} />
    </div>
  );
}

/** La différence exacte rendue par l'API, champ par champ ; les champs de provenance sont repliés à part. */
export function Difference({ difference: brute, element }: { difference: Changement[]; element: string }) {
  // Une création (le changement porte sur l’élément entier) se lit champ par champ (T6) ; le reste est inchangé.
  const difference = brute.flatMap((d) => (d.chemin === element ? aplatirChangement(d) : [d]));
  const utiles = difference.filter((d) => !estTechnique(d, element));
  const techniques = difference.filter((d) => estTechnique(d, element));
  const tableau = (lignes: Changement[], legende: string) => (
    <div className="tableau-defile">
      <table className="diff">
        <caption className="sr">{legende}</caption>
        <thead>
          <tr>
            <th scope="col">Champ</th>
            <th scope="col">Avant</th>
            <th scope="col">Après</th>
          </tr>
        </thead>
        <tbody>
          {lignes.map((d) => (
            <tr key={d.chemin}>
              <td className="chemin">{cheminLisible(d, element)}</td>
              <td className="avant">{rendu(d.avant, d.chemin)}</td>
              <td className="apres">{rendu(d.apres, d.chemin)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
  return (
    <div className="pile" style={{ gap: 8 }}>
      {utiles.length ? tableau(utiles, 'Changements') : <p className="note">Aucun changement de valeur.</p>}
      {techniques.length > 0 && (
        <details>
          <summary>
            Version et provenance ({techniques.length} champ{techniques.length > 1 ? 's' : ''})
          </summary>
          {tableau(techniques, 'Changements de version et de provenance')}
        </details>
      )}
    </div>
  );
}

function rendu(v: unknown, chemin: string): string {
  if (typeof v === 'string' && /(?:^|\.)(?:localisateur|locator)$/.test(chemin)) return localisateurAffiche(v);
  if (v === undefined || v === null) return '—';
  if (typeof v === 'object' && !('valeur' in (v as object))) return jsonAvecCitationsAffichees(v);
  return valeurLisible(v);
}
